/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';

import styles from './TopBar.style';

import { noBorder } from './setting/topBarVariants';
import PageContextualLink from 'src/shared/ui/component/link/PageContextualLink';
import Heading from 'src/shared/ui/component/text/Heading';

import type { TopBarProps } from './type/topBarTypes';

type Props = TopBarProps;

const useStyles = createUseStyles(styles);

const TopBar = ({
  headingLevel,
  layoutClass,
  title,
  subtitle,
  withSubtitleError,
  withSubtitleWarning,
  withSubtitleSuccess,
  actionLabel,
  actionRoute,
  actionRender,
  handleAction,
  isActionDisabled,
  cancelLabel,
  cancelRoute,
  handleCancel,
  variant,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ hasTitle: !!titleElt || !!subtitle });

  const rootClasses = classNames(layoutClass, classes.root, {
    [classes['root-withAction']]: cancelLabel || actionLabel,
    [classes['root-withoutBorder']]: variant === noBorder,
  });

  const headingClasses = classNames(classes.heading, {
    [classes['heading-small']]: subtitle,
  });

  const subtitleClasses = classNames(classes.subtitle, {
    [classes['subtitle-withError']]: withSubtitleError,
    [classes['subtitle-withWarning']]: withSubtitleWarning,
    [classes['subtitle-withSuccess']]: withSubtitleSuccess,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const titleElt = title && (
    <Heading layoutClass={headingClasses} level={headingLevel}>
      {title}
    </Heading>
  );

  const subtitleElt = subtitle && (
    <div className={subtitleClasses}>{subtitle}</div>
  );

  const cancelElt = cancelLabel && (
    <PageContextualLink
      layoutClass={classNames(classes.link, classes.cancel)}
      href={cancelRoute}
      handleClick={handleCancel}
    >
      <span className={classes.label}>{cancelLabel}</span>
    </PageContextualLink>
  );

  const actionElt =
    actionRender ||
    (actionLabel && (
      <PageContextualLink
        layoutClass={classNames(classes.link, classes.action)}
        href={actionRoute}
        handleClick={handleAction}
        isDisabled={isActionDisabled}
      >
        <span className={classes.label}>{actionLabel}</span>
      </PageContextualLink>
    ));

  const cancelCtn = (cancelElt || actionElt) && (
    <div className={classNames(classes.ctn, classes.cancelCtn)}>
      {cancelElt}
    </div>
  );

  const actionCtn = (cancelElt || actionElt) && (
    <div className={classNames(classes.ctn, classes.actionCtn)}>
      {actionElt}
    </div>
  );

  return (
    <header className={rootClasses}>
      {cancelCtn}
      <div className={classes.headings}>
        {titleElt}
        {subtitleElt}
      </div>
      {actionCtn}
    </header>
  );
};

// Make variants available
export * from './setting/topBarVariants';

export default compose(React.memo)(TopBar);
